import { Accordion, AccordionButton, AccordionItem, AccordionPanel, Badge, Card, Divider, HStack, IconButton, Input, Select, Text, VStack, useColorModeValue } from '@chakra-ui/react';
import React, { FC } from 'react';
import { AnswerType, Question, QuestionType } from './types';
import QuestionTypeIcon from './QuestionTypeIcon';
import { isEnum } from '../../util/enums';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import { getAnswerTypeLabel, getQuestionTypeLabel } from '../../util/string';

interface QuestionEditorProps {
  id: number | string;
  index: number;
  question: Question;
  onUpdate: (updatedQuestion: Question) => void;
  readOnly?: boolean;
}
const QuestionEditor: FC<QuestionEditorProps> = ({
  id,
  index,
  question,
  onUpdate,
  readOnly = false,
}) => {
  const [options, setOptions] = React.useState<string[]>(question.answerOptions);

  const addOption = () => {
    setOptions([...options, '']);
  };

  const removeOption = (index: number) => {
    const updatedOptions = options.filter((_, i) => i !== index);
    setOptions(updatedOptions);
  };

  const updateOption = (index: number, value: string) => {
    const updatedOptions = [...options];
    updatedOptions[index] = value;
    setOptions(updatedOptions);
    onUpdate({
      ...question,
      answerOptions: updatedOptions,
    });
  };

  const setQuestionText = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    onUpdate({
      ...question,
      value,
    });
  };

  const setQuestionType = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (!isEnum(value, QuestionType)) {
      return;
    }

    onUpdate({
      ...question,
      questionType: value as any,
    });
  };

  const setAnswerType = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;

    if (!isEnum(value, AnswerType)) {
      return;
    }

    const answerType = value as AnswerType;

    onUpdate({
      ...question,
      answerType,
    });
  };

  return (
    <Accordion allowMultiple w='container.sm'>
      <AccordionItem alignItems='center' border='none' isDisabled={readOnly}>
        <Card
          minW='full'
          w="full"
          p={2}
          variant='outline'
          bg={useColorModeValue('white', 'gray.600')}
        >
          <AccordionButton display='flex' justifyContent='space-between'>
            <HStack display='grid' gridTemplateColumns={'auto auto'} justifyContent='space-between' w='full' align='start'>
              <HStack align='start' gap={3}>
                <Badge
                  colorScheme='brand'
                  variant='outline'
                  borderRadius='50%'
                  minW={5}
                  width={5}
                  height={5}
                  justifyContent='center'
                  alignItems='center'
                  display='flex'
                >
                  {index}
                </Badge>
                <Text
                  w='full'
                  align='start'
                  fontSize='sm'
                  fontWeight='semibold'
                >
                  {question.value || 'Pregunta'}
                </Text>
              </HStack>
              <QuestionTypeIcon questionType={question.questionType} />
            </HStack>
          </AccordionButton>
          <AccordionPanel>

            <Text fontSize='sm' fontWeight='semibold' mb={1}>Pregunta</Text>
            <Input
              placeholder=''
              mb={4}
              value={question.value}
              onChange={setQuestionText}
            />

            <HStack>
              <VStack align='start' w='full' gap={0}>

                <Text fontSize='sm' fontWeight='semibold' mb={1}>Tipo de pregunta</Text>
                <Select
                  mb={4}
                  value={question.questionType}
                  onChange={setQuestionType}
                >
                  {Object.entries(QuestionType).map(([key, value]) => (
                    <option key={key} value={value}>
                      {getQuestionTypeLabel(value)}
                    </option>
                  ))}
                </Select>

              </VStack>
              <VStack align='start' w='full' gap={0}>

                <Text fontSize='sm' fontWeight='semibold' mb={1}>Tipo de respuesta</Text>
                <Select
                  mb={4}
                  disabled={question.questionType !== QuestionType.MULTIPLE}
                  value={question.answerType}
                  onChange={setAnswerType}
                >
                  {Object.entries(AnswerType).map(([key, value]) => (
                    <option key={key} value={value}>
                      {getAnswerTypeLabel(value)}
                    </option>
                  ))}
                </Select>
              </VStack>
            </HStack>

            {question.questionType === QuestionType.MULTIPLE && (
              <>
                <Divider mb={2} />
                <HStack mb={2} justify='space-between'>
                  <Text fontSize='sm' fontWeight='semibold' mb={1}>Opciones</Text>
                  <IconButton
                    aria-label='Agregar opción'
                    isRound
                    icon={<FiPlus />}
                    variant='ghost'
                    onClick={addOption}
                  />
                </HStack>
                {!options.length && (
                  <Text align='center' fontSize='xs' color='gray.500' mb={3} fontWeight='medium'>No hay opciones</Text>
                )}
                <VStack key={index} mb={2} align='start'>
                  {options.map((option, index) => (
                    <VStack align='start' key={index}>
                      <HStack gap={4}>
                        <Badge
                          colorScheme='brand'
                          variant='solid'
                          borderRadius='50%'
                          minW={5}
                          width={5}
                          height={5}
                          justifyContent='center'
                          alignItems='center'
                          display='flex'
                        >
                          {index + 1}
                        </Badge>
                        <Input
                          placeholder={`Opción ${index + 1}`}
                          value={option}
                          onChange={(e) => updateOption(index, e.target.value)}
                        />
                        <IconButton
                          aria-label='Eliminar opción'
                          isRound
                          icon={<FiTrash2 />}
                          variant='ghost'
                          onClick={() => removeOption(index)}
                        />
                      </HStack>
                    </VStack>
                  ))}
                </VStack>
              </>
            )}
            {/* 
            <Text fontSize='sm' fontWeight='semibold' mb={1}>Vista previa</Text>
            <AnswerPreview
              questionType={question.questionType}
              answerType={question.answerType}
              options={question.answerOptions}
            />
          */}
          </AccordionPanel>
        </Card >
      </AccordionItem>
    </Accordion>
  )
};

export default QuestionEditor;
